/**
 * NotificationCard Component
 * FindMyLawyer – Lawyer Portal
 *
 * Single notification row/card with:
 * - Icon (type-specific, colored)
 * - Priority color indicator dot
 * - Title, message, relative time
 * - Action button (URL resolved on frontend)
 * - Mark as read + Archive buttons
 * - Slide-out animation on archive
 */

import React, { useState } from "react";
import {
  Bell, Bot, Calendar, CheckCircle, FileText, Headphones,
  IndianRupee, Info, MessageCircle, ShieldCheck, Star,
  Video, Wallet, XCircle, X, Check, Eye,
} from "lucide-react";
import { getNotificationConfig, PRIORITY_COLORS, resolveActionUrl } from "../utils/notificationConfig";
import { useNotifications } from "../context/NotificationContext";

// ── Icon resolver ─────────────────────────────────────────────────────────────
const ICON_MAP = {
  Bell, Bot, Calendar, CheckCircle, FileText, Headphones,
  IndianRupee, Info, MessageCircle, ShieldCheck, Star,
  Video, Wallet, XCircle,
};

function NotifIcon({ name, size = 18 }) {
  const Comp = ICON_MAP[name] || Bell;
  return <Comp size={size} />;
}

// ── Relative time helper ──────────────────────────────────────────────────────
function relativeTime(dateStr) {
  const now   = Date.now();
  const then  = new Date(dateStr).getTime();
  const diff  = Math.floor((now - then) / 1000);

  if (diff < 60)           return "Just now";
  if (diff < 3600)         return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400)        return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7)   return `${Math.floor(diff / 86400)}d ago`;

  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "numeric", month: "short",
  });
}

// ── NotificationCard ──────────────────────────────────────────────────────────
export const NotificationCard = ({ notification, onNavigate }) => {
  const { markRead, archiveNotification } = useNotifications();
  const [archiving, setArchiving] = useState(false);

  const config   = getNotificationConfig(notification.type);
  const priority = PRIORITY_COLORS[notification.priority] || PRIORITY_COLORS.LOW;
  const isUnread = notification.status === "UNREAD";

  const actionUrl = resolveActionUrl(
    notification.source_type,
    notification.source_id,
    notification
  );

  const handleAction = () => {
    if (isUnread) markRead(notification.id);
    if (actionUrl && onNavigate) {
      // Map URL path to page key
      const pathToPage = {
        "/lawyer/bookings":  "bookings",
        "/lawyer/chat":      "chat",
        "/lawyer/cases":     "cases",
        "/lawyer/schedule":  "schedule",
        "/lawyer/payments":  "payments",
        "/lawyer/ai-help":   "ai-help",
        "/lawyer/settings":  "settings",
      };
      const page = pathToPage[actionUrl];
      if (page) onNavigate(page);
    }
  };

  const handleArchive = async () => {
    setArchiving(true);
    await archiveNotification(notification.id);
    // archiving state stays true to hide the card (optimistic)
  };

  if (archiving) return null; // Optimistic removal

  return (
    <div
      className={`notification-card ${isUnread ? "notification-card--unread" : ""}`}
      style={{
        display:       "flex",
        gap:           "12px",
        padding:       "14px 16px",
        borderRadius:  "12px",
        border:        `1px solid ${isUnread ? priority.border : "#e2e8f0"}`,
        background:    isUnread ? priority.bg : "#ffffff",
        marginBottom:  "8px",
        transition:    "all 0.2s ease",
        position:      "relative",
        cursor:        "default",
      }}
    >
      {/* Unread indicator strip */}
      {isUnread && (
        <div style={{
          position:     "absolute",
          left:         0,
          top:          "12px",
          bottom:       "12px",
          width:        "3px",
          borderRadius: "0 2px 2px 0",
          background:   config.color,
        }} />
      )}

      {/* Icon */}
      <div style={{
        width:          "40px",
        height:         "40px",
        borderRadius:   "10px",
        background:     config.bgColor,
        color:          config.color,
        display:        "flex",
        alignItems:     "center",
        justifyContent: "center",
        flexShrink:     0,
        border:         `1px solid ${config.color}22`,
      }}>
        <NotifIcon name={config.icon} size={18} />
      </div>

      {/* Body */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "8px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "6px", minWidth: 0 }}>
            {/* Priority dot */}
            <div style={{
              width:        "6px",
              height:       "6px",
              borderRadius: "50%",
              background:   priority.dot,
              flexShrink:   0,
            }} />
            <p style={{
              fontWeight:   isUnread ? 700 : 500,
              fontSize:     "13px",
              color:        "#0f172a",
              lineHeight:   "1.3",
              overflow:     "hidden",
              textOverflow: "ellipsis",
              whiteSpace:   "nowrap",
            }}>
              {notification.title}
            </p>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "4px", flexShrink: 0 }}>
            <span style={{ fontSize: "11px", color: "#94a3b8", whiteSpace: "nowrap" }}>
              {relativeTime(notification.created_at)}
            </span>

            {/* Mark read button */}
            {isUnread && (
              <button
                onClick={() => markRead(notification.id)}
                title="Mark as read"
                style={{
                  width: "24px", height: "24px", borderRadius: "6px",
                  background: "none", border: "1px solid #e2e8f0",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "#64748b", cursor: "pointer", transition: "all 0.15s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#f1f5f9"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "none"; }}
              >
                <Eye size={12} />
              </button>
            )}

            {/* Archive button */}
            <button
              onClick={handleArchive}
              title="Archive"
              style={{
                width: "24px", height: "24px", borderRadius: "6px",
                background: "none", border: "1px solid #e2e8f0",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "#64748b", cursor: "pointer", transition: "all 0.15s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#fef2f2"; e.currentTarget.style.color = "#ef4444"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "none"; e.currentTarget.style.color = "#64748b"; }}
            >
              <X size={12} />
            </button>
          </div>
        </div>

        {/* Message */}
        <p style={{
          fontSize:     "12px",
          color:        "#64748b",
          marginTop:    "3px",
          lineHeight:   "1.45",
        }}>
          {notification.message}
        </p>

        {/* Action button */}
        {notification.action_label && actionUrl && (
          <button
            onClick={handleAction}
            style={{
              marginTop:    "8px",
              padding:      "5px 12px",
              borderRadius: "7px",
              background:   config.color,
              color:        "#fff",
              fontSize:     "11.5px",
              fontWeight:   600,
              border:       "none",
              cursor:       "pointer",
              transition:   "opacity 0.15s",
              display:      "inline-flex",
              alignItems:   "center",
              gap:          "5px",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.85"; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
          >
            {notification.action_label}
          </button>
        )}
      </div>
    </div>
  );
};
